import { useMemo } from 'react';
import type { ModelDefinition, ModelTier } from '@mem-ai-hub/shared';
import { MODEL_TIERS } from '@mem-ai-hub/shared';

interface ModelSelectorProps {
  models: ModelDefinition[];
  selectedModelId: string | null;
  onSelect: (id: string) => void;
  disabled: boolean;
  hiddenModelIds?: string[];
  showDescriptions?: boolean;
}

const PROVIDER_LABELS: Record<string, string> = {
  anthropic: 'Anthropic',
  openai: 'OpenAI',
  xai: 'xAI',
};

export default function ModelSelector({
  models,
  selectedModelId,
  onSelect,
  disabled,
  hiddenModelIds = [],
  showDescriptions = true,
}: ModelSelectorProps) {
  const visibleModels = useMemo(
    () => models.filter((m) => m.enabled && !hiddenModelIds.includes(m.id)),
    [models, hiddenModelIds],
  );

  const groupedModels = useMemo(() => {
    const groups = new Map<ModelTier, ModelDefinition[]>();
    for (const model of visibleModels) {
      const existing = groups.get(model.tier) ?? [];
      existing.push(model);
      groups.set(model.tier, existing);
    }
    return groups;
  }, [visibleModels]);

  const tiers = useMemo(
    () => Array.from(groupedModels.keys()).sort((a, b) => a - b),
    [groupedModels],
  );

  return (
    <div className="relative">
      <label
        htmlFor="model-selector"
        className="block text-sm font-medium text-text-secondary mb-1 font-heading"
      >
        Model
      </label>
      <div className="relative">
        <select
          id="model-selector"
          value={selectedModelId ?? ''}
          onChange={(e) => onSelect(e.target.value)}
          disabled={disabled}
          className={`
            w-full appearance-none rounded-md border border-border bg-bg px-3 py-2 pr-10
            text-sm font-body text-text-primary
            focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary
            ${disabled ? 'cursor-not-allowed bg-bg-secondary text-text-muted opacity-70' : 'cursor-pointer hover:border-primary-light'}
          `}
        >
          <option value="" disabled>
            Select a model...
          </option>
          {tiers.map((tier) => (
            <optgroup key={tier} label={MODEL_TIERS[tier].name}>
              {groupedModels.get(tier)?.map((model) => (
                <option key={model.id} value={model.id}>
                  {model.displayName} ({PROVIDER_LABELS[model.provider] ?? model.provider})
                </option>
              ))}
            </optgroup>
          ))}
        </select>

        {/* Dropdown arrow / lock icon */}
        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
          {disabled ? (
            <svg
              className="h-4 w-4 text-text-muted"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
          ) : (
            <svg
              className="h-4 w-4 text-text-muted"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19 9l-7 7-7-7"
              />
            </svg>
          )}
        </div>
      </div>

      {/* Selected model description */}
      {showDescriptions && selectedModelId && (
        <SelectedModelInfo models={models} selectedModelId={selectedModelId} />
      )}
    </div>
  );
}

function SelectedModelInfo({
  models,
  selectedModelId,
}: {
  models: ModelDefinition[];
  selectedModelId: string;
}) {
  const model = models.find((m) => m.id === selectedModelId);
  if (!model?.description) return null;

  return (
    <p className="mt-1 text-xs text-text-muted">{model.description}</p>
  );
}
